/**
 * ===========================================================================
 *  A SZERZŐDÉS
 * ===========================================================================
 *
 * Ez az egyetlen fájl, amit minden modul ismer. Minden más modul kizárólag
 * az itt rögzített típusokon keresztül érintkezik a többivel.
 *
 * Gazda: C (Claude). **X ne módosítsa.** Ha változtatás kell benne,
 * kérd a `parbeszed/` mappában, és C átvezeti.
 *
 * Konvenciók:
 *  - Minden hossz „egység" = kb. 1 méter. A játékos sugara 0.45, magassága 1.8.
 *  - Y felfelé nő. Az `Actor.pos` a **láb** pozíciója, nem a középpont.
 *  - A szögek radiánban, `yaw` = 0 a +Z tengely felé néz, CCW pozitív.
 *  - Minden időzítés `tick`-ben (1 tick = 1/60 s), nem milliszekundumban.
 * ===========================================================================
 */

import type { Vector3 } from 'three'
import type { Rng } from './rng'
import type { EventBus } from './events'
import type { Vec2 } from './math'
import type { LevelDef, KeyColor, SfxId } from '../world/level'

export type { KeyColor, SfxId, TexId } from '../world/level'

/** Szimulációs frekvencia. Minden játékmeneti időzítés ehhez igazodik. */
export const TICK_RATE = 60
export const TICK_DT = 1 / TICK_RATE

/** Másodpercet tickre vált — tartalmi táblákban ez olvashatóbb. */
export function secs(s: number): number {
  return Math.max(1, Math.round(s * TICK_RATE))
}

// ---------------------------------------------------------------------------
// Aktorok
// ---------------------------------------------------------------------------

export type EntityId = number
export type Faction = 'player' | 'enemy' | 'neutral'
export type ActorKind = 'player' | 'enemy' | 'pickup' | 'decor' | 'projectile'

export type DamageKind = 'bullet' | 'pellet' | 'melee' | 'fire' | 'plasma' | 'explosion' | 'hazard'
export type ImpactKind = 'concrete' | 'metal' | 'flesh' | 'energy'

export interface Actor {
  readonly id: EntityId
  kind: ActorKind
  /** Archetípus-azonosító, pl. 'imp', 'shotgun', 'medkit'. */
  type: string
  faction: Faction

  /** A láb pozíciója. Írható, de mozgatáshoz használd a `world.moveActor`-t. */
  pos: Vector3
  vel: Vector3
  yaw: number

  radius: number
  height: number
  /** Szemmagasság a lábtól — látóvonal- és lövéskiindulás. */
  eyeHeight: number

  health: number
  maxHealth: number
  alive: boolean
  onGround: boolean
  /** Ütközik-e más aktorokkal. Hullák és pickupok: false. */
  solid: boolean
  /** Sebezhető-e egyáltalán (dekor: false). */
  vulnerable: boolean

  /** Melyik szobában van; a mozgásrendszer tartja karban. */
  roomId: number
  /** Halál óta eltelt tickek — a hulla-animációhoz és a takarításhoz. */
  deadFor: number
  /** Létrejötte óta eltelt tickek. */
  age: number

  /**
   * A tulajdonos rendszer szabad állapotmezője (AI-állapot, fegyverállapot).
   * A gazdamodul castolja a saját típusára. Más modul ne írja.
   */
  data: unknown

  /**
   * Animációs jelzések a rendererhez. A játéklogika ÍRJA, a renderer OLVASSA.
   * Szándékosan minimális: a modell mozgását a sebesség és a halál állapota
   * magától kiadja, csak ezt a kettőt nem lehet kitalálni kívülről.
   */
  anim: {
    /** 0..1 — támadás előtti telegráf feltöltöttsége. */
    telegraph: number
    /** 0..1 — fájdalom-visszarándulás; a renderer magától csengeti le. */
    pain: number
  }

  /** Interpolációhoz — a szimuláció eleje írja, a renderer olvassa. */
  prevPos: Vector3
  prevYaw: number

  /** A renderer belső fogantyúja. Ehhez a játéklogika NE nyúljon. */
  view: unknown
}

export interface SpawnSpec {
  kind: ActorKind
  type: string
  faction: Faction
  x: number
  y: number
  z: number
  yaw?: number
  radius: number
  height: number
  eyeHeight?: number
  health?: number
  solid?: boolean
  vulnerable?: boolean
  data?: unknown
}

// ---------------------------------------------------------------------------
// Világ (statikus geometria, kollízió, ajtók)
// ---------------------------------------------------------------------------

export interface MoveResult {
  /** Ténylegesen elmozdult-e. */
  moved: boolean
  /** Falnak/propnak/csukott ajtónak ütközött-e. */
  hitWall: boolean
  /** Az utolsó ütközés normálisa az XZ-síkon, vagy null. */
  wallNormal: Vec2 | null
  /** Mennyit lépett fel (0, ha semmit). */
  steppedUp: number
  /** Aktornak ütközött-e (és melyiknek). */
  blockedBy: Actor | null
}

export interface WorldHit {
  distance: number
  point: Vector3
  normal: Vector3
  surface: 'wall' | 'floor' | 'ceiling' | 'prop' | 'door'
  /** Anyagjelleg a becsapódás-effekthez. */
  impact: ImpactKind
}

export interface ActorHit {
  actor: Actor
  distance: number
  point: Vector3
}

export type UseResult = 'none' | 'opened' | 'locked' | 'switched' | 'exit'

export interface WorldApi {
  readonly level: LevelDef

  /**
   * Csúsztatott mozgatás az XZ-síkon, aktor-aktor ütközéssel együtt.
   * Módosítja `actor.pos`-t és `actor.roomId`-t. A függőleges mozgást
   * (gravitáció, padlóhoz tapadás) a hívó végzi a `floorAt` alapján.
   */
  moveActor(actor: Actor, dx: number, dz: number, stepHeight: number): MoveResult

  /** Sugárkövetés a statikus világ ellen (falak, padló, mennyezet, propok, ajtók). */
  raycast(origin: Vector3, dir: Vector3, maxDist: number): WorldHit | null

  /** Melyik szobában van a pont. `hint` a valószínű szoba — gyorsítás. */
  roomAt(x: number, z: number, hint?: number): number
  /** A padló magassága; figyelembe veszi a mozgó lifteket. */
  floorAt(x: number, z: number, hint?: number): number
  ceilAt(x: number, z: number, hint?: number): number
  /** Sebzés/mp, ha valaki itt áll a padlón (savas, mérgező tér). 0 = biztonságos. */
  hazardAt(x: number, z: number, hint?: number): number

  /** Akadálytalan-e a látóvonal? (Csak statikus geometria + csukott ajtók.) */
  lineOfSight(from: Vector3, to: Vector3): boolean

  /**
   * Járható-e egyenesen A-ból B-be `radius` sugarú testtel? Figyelembe veszi
   * a falakat és a lépésmagasságnál nagyobb szintkülönbséget. AI-navigációhoz.
   */
  canWalk(from: Vector3, to: Vector3, radius: number, stepHeight: number): boolean

  /** Ajtó/kapcsoló aktiválása a nézésirány mentén (a „használat" gomb). */
  tryUse(origin: Vector3, dir: Vector3, maxDist: number, keys: ReadonlySet<KeyColor>): UseResult

  /** Ajtó/lift távoli nyitása trigger-tag alapján. */
  triggerTag(tag: string): boolean
}

// ---------------------------------------------------------------------------
// Aktor-nyilvántartás
// ---------------------------------------------------------------------------

export interface ActorsApi {
  /** A játékos aktora. Mindig létezik, amíg pálya van betöltve. */
  readonly player: Actor

  all(): readonly Actor[]
  get(id: EntityId): Actor | undefined
  spawn(spec: SpawnSpec): Actor
  remove(id: EntityId): void

  /**
   * Élő aktorok egy körben (XZ). `faction === null` esetén mind.
   * Az eredmény `out`-ba kerül (újrahasznosítható tömb), és vissza is adjuk.
   */
  query(x: number, z: number, radius: number, faction: Faction | null, out: Actor[]): Actor[]

  /** Első aktor-találat egy sugáron. Csak `solid && alive` aktorokat néz. */
  raycast(
    origin: Vector3,
    dir: Vector3,
    maxDist: number,
    ignore: Actor | null,
    faction: Faction | null,
  ): ActorHit | null

  /**
   * EGYSÉGES sebzési belépési pont. Ez kezeli a páncélt, a halált, a
   * `damage`/`death` eseményt és a találat-visszajelzést.
   * Sebezni MINDIG ezen keresztül kell — ne írd közvetlenül a `health`-et.
   */
  damage(target: Actor, amount: number, source: Actor | null, dir: Vector3 | null, kind: DamageKind): void

  /** Területi sebzés négyzetes csillapítással; a robbanásokhoz. */
  splash(
    center: Vector3,
    radius: number,
    maxDamage: number,
    source: Actor | null,
    kind: DamageKind,
    selfDamageFactor?: number,
  ): void
}

// ---------------------------------------------------------------------------
// Bemenet
// ---------------------------------------------------------------------------

export interface InputState {
  /** -1..1, jobbra pozitív. */
  moveX: number
  /** -1..1, előre pozitív. */
  moveZ: number
  /** Az utolsó tick óta felhalmozott egér-elmozdulás radiánban. */
  lookYaw: number
  lookPitch: number

  fire: boolean
  /** Csak abban a tickben igaz, amikor lenyomták. */
  firePressed: boolean
  usePressed: boolean
  run: boolean
  /** 1..9, vagy null. Csak egy tickig él. */
  weaponSlot: number | null
  weaponCycle: number
  /** Pointer lock aktív-e; ha nem, a játék szünetel. */
  active: boolean
}

// ---------------------------------------------------------------------------
// Effektek (a játéklogika ezen keresztül kér hangot, részecskét, rázást)
// ---------------------------------------------------------------------------

export interface FxApi {
  /** `pos === null` esetén a hang nem pozicionált (UI, játékos fegyvere). */
  sound(id: SfxId, pos?: Vector3 | null, volume?: number, pitch?: number): void
  muzzleFlash(pos: Vector3, dir: Vector3, color: number, radius: number): void
  impact(point: Vector3, normal: Vector3, kind: ImpactKind): void
  blood(point: Vector3, dir: Vector3, amount: number): void
  explosion(point: Vector3, radius: number, color?: number): void
  /** Nyomjelző csík hitscan lövéshez. */
  tracer(from: Vector3, to: Vector3, color: number, width?: number): void
  /** Kamerarázás; az értékek összeadódnak, majd lecsengenek. */
  shake(amount: number): void
  /** Teljes képernyős villanás (sebzés = vörös, felvétel = arany). */
  screenFlash(color: number, strength: number): void
  hitMarker(lethal: boolean): void
  /** Rövid üzenet a HUD tetején. */
  message(text: string): void
}

// ---------------------------------------------------------------------------
// HUD-állapot: X írja, C rajzolja
// ---------------------------------------------------------------------------

export interface HudWeaponSlot {
  slot: number
  id: string
  owned: boolean
  hasAmmo: boolean
}

export interface HudState {
  health: number
  maxHealth: number
  armor: number
  /** null = a fegyver nem használ lőszert (ököl). */
  ammo: number | null
  ammoMax: number | null
  ammoType: string
  weaponId: string
  weaponName: string
  weapons: HudWeaponSlot[]
  keys: KeyColor[]
  enemiesTotal: number
  enemiesLeft: number
  secretsFound: number
  secretsTotal: number
  /** 0..1 — a fegyver újratöltési/ciklusidejének kitöltöttsége. */
  fireProgress: number
  /**
   * Fut-e éppen az élet-regeneráció. A HUD ebből pulzáltatja az
   * élet-számot, hogy a játékos lássa: az óra újraindult és tölt.
   * (X kérése — `13-X-jatekos-valasz.md`.)
   */
  regenActive: boolean
}

// ---------------------------------------------------------------------------
// Kézifegyver-modell (a képernyő alján látszó fegyver)
// ---------------------------------------------------------------------------

export interface ViewModelApi {
  /** Fegyverváltás — lehúzás/felemelés animációval. */
  equip(weaponId: string): void
  /** Elsütés: visszarúgás + torkolattűz-pozíció. */
  fire(recoil: number): void
  /** A torkolat világkoordinátája — innen indul a nyomjelző. */
  muzzleWorld(out: Vector3): Vector3
  /** 0..1: mennyire van „lent" a fegyver (váltás közben). */
  readonly lowered: number
}

// ---------------------------------------------------------------------------
// Kamera: a játékos-rendszer írja, a renderer olvassa
// ---------------------------------------------------------------------------

export interface CameraState {
  /** Szemmagasságban, világkoordinátában. */
  pos: Vector3
  yaw: number
  pitch: number
  roll: number
  fov: number
}

// ---------------------------------------------------------------------------
// Események
// ---------------------------------------------------------------------------

export interface GameEventMap extends Record<string, unknown> {
  damage: { target: Actor; amount: number; source: Actor | null; kind: DamageKind; dir: Vector3 | null }
  death: { actor: Actor; source: Actor | null }
  spawn: { actor: Actor }
  pickup: { type: string; by: Actor }
  keyTaken: { color: KeyColor }
  doorOpened: { portalId: number }
  secretFound: { roomId: number }
  levelExit: { levelId: string }
  playerDied: { by: string }
}

// ---------------------------------------------------------------------------
// Játékállapot és futásidejű kontextus
// ---------------------------------------------------------------------------

export type GameState = 'title' | 'briefing' | 'playing' | 'dead' | 'won' | 'paused'

export interface Ctx {
  /** Fix szimulációs lépésköz másodpercben (1/60). */
  readonly dt: number
  /** Az aktuális szimulációs tick sorszáma a pálya kezdete óta. */
  tick: number

  readonly rng: Rng
  readonly world: WorldApi
  readonly actors: ActorsApi
  readonly fx: FxApi
  readonly events: EventBus<GameEventMap>
  readonly input: InputState
  readonly hud: HudState
  readonly view: ViewModelApi
  readonly camera: CameraState

  state: GameState
  /** A jelenlegi pálya indexe a kampányban. */
  levelIndex: number
}

/**
 * Egy játékrendszer. A `fixedUpdate` fix lépésközzel fut (ide való MINDEN
 * játéklogika), a `frameUpdate` keretenként egyszer, interpolációval —
 * oda csak vizuális simítás való (kamera, view model), állapotváltozás nem.
 */
export interface System {
  readonly name: string
  init?(ctx: Ctx): void
  fixedUpdate(ctx: Ctx): void
  frameUpdate?(ctx: Ctx, alpha: number, dtReal: number): void
  /** Pályaváltáskor / újraindításkor. */
  reset?(ctx: Ctx): void
  dispose?(): void
}
